import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Vector2 as BABYLON_Vector2 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
/**
 * This class is meant to help with rendering to a smaller portion of a render target.
 * Traditionally, what you would do is bind your render target, and then use glViewport to only render to a portion. But Babylon doesn't expose a good way to do so.
 *  - And you don't want to resize your render targets/textures often, as its a fairly expensive process.
 *
 * This class works by spoofing the resolution of the underlying texture of the render target in the Babylon data.
 *
 * This allows you to use one render target for multiple effects that are ran in different resolutions, for example using the same buffer for a half-resolution effect and a full-resolution effect.
 * It was added to let us render outlines using the same render targets for all Views (that potentially have different resolutions).
 */
export class PartialViewportRenderTarget {
    constructor(name, size, scene, generate_mipmaps, do_not_change_aspect_ratio, type, is_cube, sampling_mode, generate_depth_buffer, generate_stencil_buffer, is_multi, format, delay_allocation) {
        this.should_render = true;
        this._render_target = new BABYLON_RenderTargetTexture(name, size, scene, generate_mipmaps, do_not_change_aspect_ratio, type, is_cube, sampling_mode, generate_depth_buffer, generate_stencil_buffer, is_multi, format, delay_allocation);
        this._actual_width = this._render_target.getRenderWidth();
        this._actual_height = this._render_target.getRenderHeight();
        const original_should_render = this._render_target._shouldRender.bind(this._render_target);
        this._render_target._shouldRender = () => {
            return this.should_render && original_should_render();
        };
    }
    /**
     * Gets the render target at the specified size, ONLY FOR TEMPORARY USE!
     * Automatically increases the size if needed.
     * @param width The wanted dimensions
     * @param height The wanted dimensions
     * @returns
     */
    get_temporary_partial_render_target(width, height) {
        if (width > this._actual_width || height > this._actual_height) {
            // We need to increase our size.
            this.set_actual_size(width, height);
        }
        else {
            const size = { width: width, height: height };
            // @ts-ignore
            this._render_target._size = size;
            this._render_target._texture.width = width;
            this._render_target._texture.height = height;
            // @ts-ignore
            this._render_target.renderTarget._size = size;
        }
        return this._render_target;
    }
    /**
     * Can be seen as a resize or "shrink to fit" function. Useful if your future resolutions will be lower.
     * Should be used carefully, as it will reallocate GPU memory.
     * @param width
     * @param height
     */
    set_actual_size(width, height) {
        this._render_target.resize({ width: width, height: height });
        this._actual_width = width;
        this._actual_height = height;
    }
    get_actual_size() {
        return {
            x: this._actual_width,
            y: this._actual_height
        };
    }
    get render_target() {
        return this._render_target;
    }
    get_full_to_partial_scale() {
        const k = 0.0;
        return new BABYLON_Vector2((this.render_target.getSize().width - k) / (this._actual_width - k), (this.render_target.getSize().height - k) / (this._actual_height - k));
    }
    dispose() {
        this._render_target.dispose();
    }
}
//# sourceMappingURL=PartialViewportRenderTarget.js.map